import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Star, ShoppingBag, Heart, Minus, Plus, ChevronRight, Flame, Leaf } from "lucide-react";
import { toast } from "sonner";
import { api, formatINR } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, toggleWishlist, wishlist } = useCart();
  const [product, setProduct] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const [variantSel, setVariantSel] = useState({});

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/products/${id}`);
      setProduct(data);
      const initial = {};
      (data.variants || []).forEach((v) => {
        const base = v.options.find((o) => (o.priceDelta || 0) === 0) || v.options[0];
        initial[v.type] = base;
      });
      setVariantSel(initial);
      setImgIdx(0); setQty(1);
    })();
  }, [id]);

  if (!product) return <div className="max-w-7xl mx-auto p-10 text-center text-muted-foreground">Loading…</div>;

  const priceDelta = Object.values(variantSel).reduce((s, v) => s + (v?.priceDelta || 0), 0);
  const displayPrice = product.price + priceDelta;
  const wished = wishlist.includes(product.id);
  const images = product.images && product.images.length ? product.images : [product.image];

  const handleAdd = () => {
    addToCart(product, variantSel, qty);
    toast.success(`Added ${qty} × ${product.name}`, { description: "Continue shopping or view cart" });
  };
  const handleBuyNow = () => {
    addToCart(product, variantSel, qty);
    navigate("/cart");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <nav className="text-xs text-muted-foreground mb-6 flex items-center gap-1">
        <Link to="/">Home</Link><ChevronRight className="w-3 h-3" />
        <Link to="/shop">Shop</Link><ChevronRight className="w-3 h-3" />
        <span className="text-foreground">{product.name}</span>
      </nav>

      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
        {/* Gallery */}
        <div>
          <div className="relative aspect-square rounded-3xl overflow-hidden bg-[hsl(var(--muted))] group">
            <img src={images[imgIdx]} alt={product.name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            {product.tag && (
              <span className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs uppercase tracking-widest bg-white/85 border">{product.tag}</span>
            )}
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar">
              {images.map((img, i) => (
                <button key={i} onClick={() => setImgIdx(i)}
                  className={`shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition ${imgIdx === i ? "border-candle-terracotta" : "border-transparent"}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <div className="flex items-center gap-2 text-sm mb-2">
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 fill-candle-gold text-candle-gold" />
              <span className="font-medium">{product.rating}</span>
            </div>
            <span className="text-muted-foreground">· {product.reviewsCount} reviews</span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">{product.name}</h1>
          <p className="mt-2 text-sm uppercase tracking-widest text-candle-terracotta">{product.scent}</p>

          <div className="mt-5 flex items-baseline gap-3">
            <div className="font-serif text-3xl font-semibold text-candle-espresso">{formatINR(displayPrice)}</div>
            {product.originalPrice > product.price && (
              <div className="text-sm text-muted-foreground line-through">{formatINR(product.originalPrice + priceDelta)}</div>
            )}
          </div>

          <p className="mt-5 text-muted-foreground leading-relaxed">{product.longDescription || product.description}</p>

          <div className="mt-6 grid grid-cols-3 gap-3">
            <SpecPill icon={<Flame className="w-4 h-4" />} label="Burn Time" value={product.burnTime} />
            <SpecPill icon={<Leaf className="w-4 h-4" />} label="Wax" value="100% Soy" />
            <SpecPill icon={<Star className="w-4 h-4" />} label="Size" value={product.size} />
          </div>

          {/* Variants */}
          {(product.variants || []).map((v) => (
            <div key={v.type} className="mt-6">
              <div className="text-sm font-medium mb-2">{v.label} <span className="text-muted-foreground font-normal">· {variantSel[v.type]?.name}</span></div>
              <div className="flex flex-wrap gap-2">
                {v.options.map((o) => (
                  <button key={o.name} onClick={() => setVariantSel((s) => ({ ...s, [v.type]: o }))}
                    data-testid={`variant-${v.type}-${o.name.replace(/\s+/g, '-')}`}
                    className={`px-3 py-2 rounded-full text-sm border transition ${variantSel[v.type]?.name === o.name ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]" : "border-[hsl(var(--border))] hover:border-candle-terracotta"}`}>
                    {o.name}{o.priceDelta ? <span className="text-xs opacity-80 ml-1">{o.priceDelta > 0 ? "+" : ""}{formatINR(o.priceDelta)}</span> : null}
                  </button>
                ))}
              </div>
            </div>
          ))}

          {/* Qty */}
          <div className="mt-6 flex items-center gap-4">
            <div className="text-sm font-medium">Quantity</div>
            <div className="flex items-center gap-1 border border-[hsl(var(--border))] rounded-full px-1 py-1">
              <button data-testid="qty-decrement" onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-8 h-8 rounded-full hover:bg-[hsl(var(--muted))] flex items-center justify-center"><Minus className="w-4 h-4" /></button>
              <span data-testid="qty-value" className="w-8 text-center font-medium">{qty}</span>
              <button data-testid="qty-increment" onClick={() => setQty((q) => q + 1)} className="w-8 h-8 rounded-full hover:bg-[hsl(var(--muted))] flex items-center justify-center"><Plus className="w-4 h-4" /></button>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Button data-testid="pd-add-to-cart" onClick={handleAdd} size="lg" className="flex-1 rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90">
              <ShoppingBag className="w-4 h-4 mr-2" /> Add to Cart
            </Button>
            <Button data-testid="pd-buy-now" onClick={handleBuyNow} size="lg" variant="outline" className="flex-1 rounded-full border-candle-terracotta text-candle-terracotta hover:bg-candle-terracotta hover:text-white">
              Buy Now
            </Button>
            <Button data-testid="pd-wishlist" onClick={() => { toggleWishlist(product.id); toast(wished ? "Removed" : "Saved ❤︎"); }}
              size="lg" variant="outline" className="rounded-full">
              <Heart className={`w-4 h-4 ${wished ? "fill-candle-terracotta text-candle-terracotta" : ""}`} />
            </Button>
          </div>

          <Accordion type="single" collapsible className="mt-8">
            <AccordionItem value="ingredients"><AccordionTrigger>Ingredients</AccordionTrigger>
              <AccordionContent>{(product.ingredients || []).join(" · ") || "Natural soy wax, essential oils, cotton wick."}</AccordionContent>
            </AccordionItem>
            <AccordionItem value="care"><AccordionTrigger>Burn & Care Tips</AccordionTrigger>
              <AccordionContent>Trim wick to ¼" before each burn. Burn for 3–4 hours on first light to prevent tunneling. Never leave unattended.</AccordionContent>
            </AccordionItem>
            <AccordionItem value="shipping"><AccordionTrigger>Shipping & Returns</AccordionTrigger>
              <AccordionContent>Standard shipping 3–5 business days. Free over ₹999. Easy 7-day return on unlit candles.</AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    </div>
  );
}

const SpecPill = ({ icon, label, value }) => (
  <div className="p-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]">
    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">{icon}{label}</div>
    <div className="mt-1 font-medium">{value}</div>
  </div>
);
